// COMPONENT_MANIFEST — the registry of all components.
//
// Each entry declares everything the form, the ingest pipeline, and the engine
// need to know about a component:
//   - id (the enum value)
//   - dependsOn (other components that must also be enabled)
//   - alwaysOn (dashboard is alwaysOn; user cannot un-tick)
//   - generated (does ingest produce data for this? false = runtime-only)
//   - generates (data type the AI must produce)
//   - defaultVolume (target count; templates may override)
//   - label / icon / description (form + sidebar)
//   - promptFragment (appended to SYSTEM_PROMPT only if enabled — §3 ingest parameterization)
//
// Decision (Session 19A): keep prompt fragments in this single file alongside metadata
// rather than scattered across the ingest service. Why: form preview can show
// "what AI is being asked", and a single grep tells you everything about a component.
// Prompt fragments stay short — full schema/quality rules stay in shared/schemas/.
//
// 19BH Day 2 Step 2.3 · v4 declarative fields populated (was skeleton in 2.2):
//   - generationTiming · 'initial' | 'usage' (mock_exam is the only 'usage')
//   - outlineLevel · which CurriculumOutline levels item tags must reference
//   - crossChapterAllowed · subjective items may emit caseIds[multiple]
//   - acceptsOmissionReason · schema includes omittedConcepts soft-escape (K18)
//   - regenContext · what context the regen workflow gathers (v4-rewrite §14)
//   - auditInvariants · which audit rules the no-LLM audit pass runs (v4-rewrite §13.3)
// All values trace back to docs/architecture/ingest-v4-rewrite.md §6/§13.3/§14.
import { DEFAULT_REGEN_CONTEXT_SPEC } from './schemas/regen-context-spec.js';
// ── Step 2.3 · Shared RegenContextSpec clusters (v4-rewrite.md §14 table) ────
//
// Six distinct context-gather profiles cover all stages. Inlining them as
// named constants makes each MANIFEST entry's `regenContext: OBJECTIVE_REGEN`
// reviewable at a glance (vs. repeating a 6-line object literal 11×) and
// keeps a single source of truth — change "objective stages need chapter
// source" once, all of mcq/tf/fc inherit.
//
// New stage decision tree:
//   - Discrimination-style (mcq/tf/fc-shape)? → OBJECTIVE_REGEN
//   - Comparative argument (essay/freeResp/proofDrill/reading)? → SUBJECTIVE_REGEN
//   - Vocabulary-shape (glossary)? → GLOSSARY_REGEN
//   - Course-wide cohesion (lecture_notes)? → LECTURE_REGEN
//   - Per-chapter aux (mindmap)? → MINDMAP_REGEN
//   - Top-down composite (mock_papers, guide)? → MOCK_REGEN
//   - Runtime/no-regen? → DEFAULT_REGEN_CONTEXT_SPEC
/** mcq · true_false · flashcard — discrimination items dedup within chapter,
 *  need glossary/lecture/source of that chapter to write distractors. */
const OBJECTIVE_REGEN = {
    needsFullOutline: true,
    needsSameStageInChapter: 'always',
    needsChapterLectureAndGlossary: true,
    needsChapterSource: true,
    needsFullCourseSource: false,
    needsOtherChaptersOfSameKind: false,
};
/** essay · free_response · proof_drill · reading_passage — subjective synthesis
 *  items may cross chapters, dedup within stage, need full course source. */
const SUBJECTIVE_REGEN = {
    needsFullOutline: true,
    needsSameStageInChapter: 'always',
    needsChapterLectureAndGlossary: false,
    needsChapterSource: false,
    needsFullCourseSource: true,
    needsOtherChaptersOfSameKind: false,
};
/** glossary — terms dedup within chapter, source needed for definition,
 *  but lecture/glossary not (we ARE the glossary). */
const GLOSSARY_REGEN = {
    needsFullOutline: true,
    needsSameStageInChapter: 'always',
    needsChapterLectureAndGlossary: false,
    needsChapterSource: true,
    needsFullCourseSource: false,
    needsOtherChaptersOfSameKind: false,
};
/** lecture_notes — single canonical doc per chapter, needs the most context:
 *  full course source + other chapters' lectures for cross-chapter
 *  consistency (terminology, framework alignment). */
const LECTURE_REGEN = {
    needsFullOutline: true,
    needsSameStageInChapter: 'always',
    needsChapterLectureAndGlossary: true,
    needsChapterSource: true,
    needsFullCourseSource: true,
    needsOtherChaptersOfSameKind: true,
};
/** mindmap — one tree per chapter, no dedup (single item), uses glossary +
 *  lecture (already generated) as scaffold to label nodes. */
const MINDMAP_REGEN = {
    needsFullOutline: true,
    needsSameStageInChapter: 'never',
    needsChapterLectureAndGlossary: true,
    needsChapterSource: true,
    needsFullCourseSource: false,
    needsOtherChaptersOfSameKind: false,
};
/** mock_papers · guide — top-down composite that pulls from the full course
 *  but doesn't dedup against prior mock papers (each is independent). */
const MOCK_REGEN = {
    needsFullOutline: true,
    needsSameStageInChapter: 'never',
    needsChapterLectureAndGlossary: false,
    needsChapterSource: false,
    needsFullCourseSource: true,
    needsOtherChaptersOfSameKind: false,
};
export const COMPONENT_MANIFEST = [
    {
        id: 'dashboard',
        dependsOn: [],
        alwaysOn: true,
        generated: false,
        generates: null,
        defaultVolume: null,
        family: 'runtime',
        perChapterTarget: null,
        label: { en: 'Dashboard', zh: '总览' },
        icon: '⊞',
        description: { en: 'Per-case mastery + countdown + study suggestions.', zh: '总览每个 case 的掌握度、倒计时与建议下一步。' },
        promptFragment: '',
        // runtime shell — no AI generation, no regen, no audit.
        generationTiming: 'initial',
        outlineLevel: [],
        crossChapterAllowed: false,
        acceptsOmissionReason: false,
        regenContext: DEFAULT_REGEN_CONTEXT_SPEC,
        auditInvariants: [],
    },
    {
        id: 'flashcards',
        dependsOn: [],
        alwaysOn: false,
        generated: true,
        generates: 'flashcards',
        defaultVolume: { min: 30, max: 60, unit: 'per-course' },
        family: 'generative',
        // 19AI: bumped per-chapter floor for course total ≥ 100 (7 ch × 15 = 105).
        perChapterTarget: { min: 12, ideal: 15, max: 25 },
        label: { en: 'Flashcards', zh: '闪卡' },
        icon: '◈',
        description: { en: 'Spaced-repetition drilling on key facts and frameworks.', zh: '间隔重复，巩固关键事实与框架结构。' },
        promptFragment: 'Generate flashcards (front = question, back = answer including framework structure). Each flashcard typed as "fact" | "causal" | "framework". Target 30–60 across the course.',
        generationTiming: 'initial',
        outlineLevel: ['chapter', 'topic', 'concept'],
        crossChapterAllowed: false,
        acceptsOmissionReason: true,
        regenContext: OBJECTIVE_REGEN,
        auditInvariants: [
            { kind: 'tag_in_outline', field: 'caseIds' },
            { kind: 'tag_in_outline', field: 'conceptIds' },
            { kind: 'min_per_concept', scope: 'chapter', min: 1 },
            { kind: 'dedup_by_concept', scope: 'chapter', textSimilarityThreshold: 0.85 },
            { kind: 'omission_rate', max: 0.3 },
        ],
    },
    {
        id: 'quiz_mcq',
        dependsOn: [],
        alwaysOn: false,
        generated: true,
        generates: 'mcqs',
        defaultVolume: { min: 8, max: 15, unit: 'per-case' },
        family: 'discrimination',
        // 19AI: bumped per-chapter floor for course total ≥ 100 (7 ch × 15 = 105).
        // 陛下原话: "我想一门课我们应该至少设置一个下限，比如至少是100道".
        perChapterTarget: { min: 14, ideal: 16, max: 22 },
        label: { en: 'Quiz (MCQ)', zh: '刷题 (MCQ)' },
        icon: '◉',
        description: { en: 'Multiple-choice drilling with rationale per option.', zh: '多选题刷题，每个选项给详细解析。' },
        promptFragment: 'Generate MCQs with exactly 4 options (A/B/C/D). Each MCQ has category ∈ {framework | fact | causal | strategy | comparison | concept | policy | logical_reasoning} and difficulty ∈ {easy | medium | hard}. Apply length & position balance rules. Use logical_reasoning category for premise-conclusion / validity / weaken / strengthen / assumption items (often appropriate for 考研 / GMAT-style courses; rare elsewhere). Target 8–15 per case.',
        generationTiming: 'initial',
        outlineLevel: ['chapter', 'topic', 'concept'],
        crossChapterAllowed: false,
        acceptsOmissionReason: true,
        regenContext: OBJECTIVE_REGEN,
        // v4-rewrite §13.3 mcq example · option_parity is mcq-only.
        auditInvariants: [
            { kind: 'tag_in_outline', field: 'caseIds' },
            { kind: 'tag_in_outline', field: 'conceptIds' },
            { kind: 'min_per_concept', scope: 'chapter', min: 1 },
            { kind: 'format_validity', ruleId: 'option_parity' },
            { kind: 'dedup_by_concept', scope: 'chapter', textSimilarityThreshold: 0.85 },
            { kind: 'omission_rate', max: 0.3 },
        ],
        // 19BO+ · DEBT-19BO+-DISCIPLINE-WIREUP: MCQs are the primary objective-item
        // format for 法考/司考 (statute precision in options) and 执医/期末 (INN +
        // dose in stems). Languages MCQs rely on idiom/register, not the per-rule
        // naming constraints, so languages is intentionally NOT keyed here.
        disciplineProfiles: {
            law: { additionalFormatRules: ['statute_citation'] },
            medicine: { additionalFormatRules: ['drug_nomenclature'] },
            // 19BO+ · P2-1: MCQ options benefit from citation/precision rules in
            // social science (stat reporting) and business (framework/number precision).
            // humanities/arts MCQs rely on content itself — no per-rule addendum.
            social_science: { additionalFormatRules: ['empirical_citation', 'quantitative_report'] },
            business: { additionalFormatRules: ['case_framework', 'financial_precision'] },
        },
    },
    {
        id: 'quiz_tf',
        dependsOn: ['quiz_mcq'],
        alwaysOn: false,
        generated: true,
        generates: 'true_false',
        defaultVolume: { min: 5, max: 10, unit: 'per-case' },
        family: 'discrimination',
        // 19AI: bumped (7 ch × 8 = 56 min).
        perChapterTarget: { min: 8, ideal: 10, max: 14 },
        label: { en: 'Quiz (T/F)', zh: '判断题' },
        icon: '◐',
        description: { en: 'Trap-style true/false with strict explanation prefix.', zh: '判断题，注重设陷阱；解析以 TRUE./FALSE. 起头。' },
        promptFragment: 'Generate true/false statements that test specific traps (negation flip, threshold value, scope creep). Explanation must start with "TRUE." or "FALSE.". Target 5–10 per case.',
        generationTiming: 'initial',
        outlineLevel: ['chapter', 'topic', 'concept'],
        crossChapterAllowed: false,
        acceptsOmissionReason: true,
        regenContext: OBJECTIVE_REGEN,
        // No option_parity (T/F has no options); rest mirrors mcq.
        auditInvariants: [
            { kind: 'tag_in_outline', field: 'caseIds' },
            { kind: 'tag_in_outline', field: 'conceptIds' },
            { kind: 'min_per_concept', scope: 'chapter', min: 1 },
            { kind: 'dedup_by_concept', scope: 'chapter', textSimilarityThreshold: 0.85 },
            { kind: 'omission_rate', max: 0.3 },
        ],
    },
    {
        id: 'glossary',
        dependsOn: [],
        alwaysOn: false,
        generated: true,
        generates: 'glossary',
        defaultVolume: { min: 30, max: 80, unit: 'per-course' },
        family: 'generative',
        perChapterTarget: { min: 8, ideal: 12, max: 20 },
        label: { en: 'Glossary', zh: '词汇表' },
        icon: '◎',
        description: { en: 'Term definitions with cross-references to cases.', zh: '术语定义 + 关联 case 索引。' },
        promptFragment: 'Generate a glossary of terms (id snake_case). Each entry has shortDef (one line) and longDef (paragraph). Cross-reference to caseIds. Target 30–80 across the course.',
        generationTiming: 'initial',
        outlineLevel: ['chapter', 'concept'],
        crossChapterAllowed: false,
        acceptsOmissionReason: true,
        regenContext: GLOSSARY_REGEN,
        auditInvariants: [
            { kind: 'tag_in_outline', field: 'caseIds' },
            { kind: 'tag_in_outline', field: 'conceptIds' },
            // Glossary should cover ~most concepts; 0.8 leaves space for very
            // abstract concepts that don't have a single matching term.
            { kind: 'concept_coverage', scope: 'chapter', min: 0.8 },
            { kind: 'format_validity', ruleId: 'source_ref' },
            { kind: 'dedup_by_concept', scope: 'chapter', textSimilarityThreshold: 0.85 },
            { kind: 'omission_rate', max: 0.2 },
        ],
        // 19BO+ · DEBT-19BO+-DISCIPLINE-WIREUP: glossary definitions demand the same
        // precision as item bodies — statute titles (law), INN drug names (medicine),
        // and correct target-language terms (languages) all apply here.
        disciplineProfiles: {
            law: { additionalFormatRules: ['statute_citation'] },
            medicine: { additionalFormatRules: ['drug_nomenclature'] },
            languages: { additionalFormatRules: ['transliteration'] },
            // 19BO+ · P2-1: glossary definitions carry discipline citation norms too.
            humanities: { additionalFormatRules: ['text_evidence', 'historical_context'] },
            social_science: { additionalFormatRules: ['empirical_citation', 'quantitative_report'] },
            business: { additionalFormatRules: ['case_framework', 'financial_precision'] },
            arts: { additionalFormatRules: ['visual_analysis', 'movement_context'] },
        },
    },
    {
        id: 'essay',
        dependsOn: [],
        alwaysOn: false,
        generated: true,
        generates: 'essays',
        defaultVolume: { min: 10, max: 20, unit: 'per-course' },
        family: 'synthesis',
        perChapterTarget: { min: 2, ideal: 3, max: 5 },
        label: { en: 'Essay Drill', zh: 'Essay 练习' },
        icon: '✎',
        description: { en: 'Prompt + scaffold + model answer side-by-side.', zh: '题干 + 框架脚手架 + 模板答案并排展示。' },
        promptFragment: 'Generate essay items with prompt, keyPoints[], scaffold[] (5-step framework structure), modelAnswer (paragraph), wordTarget. Frame essays as framework-application. Target 10–20 across the course.',
        generationTiming: 'initial',
        outlineLevel: ['chapter', 'concept'],
        // ★ subjective synthesis · cross-chapter framework essays expected.
        crossChapterAllowed: true,
        acceptsOmissionReason: true,
        regenContext: SUBJECTIVE_REGEN,
        auditInvariants: [
            { kind: 'tag_in_outline', field: 'caseIds' },
            { kind: 'tag_in_outline', field: 'conceptIds' },
            { kind: 'min_per_chapter', quantity: 2 },
            // Course-wide dedup — essays span chapters and should not duplicate.
            { kind: 'dedup_by_concept', scope: 'course', textSimilarityThreshold: 0.85 },
            { kind: 'omission_rate', max: 0.3 },
        ],
        // 19BO+ · DEBT-19BO+-DISCIPLINE-WIREUP: essays are the primary carrier for
        // law (论述/案例分析), medicine (伦理/病例论述) and languages (写作) — all three
        // disciplines benefit from the per-scenario rules (法考/期末/IRAC · 执医/USMLE ·
        // 考研雅思/HSK). See discipline-profile.ts DISCIPLINE_FORMAT_RULES.
        disciplineProfiles: {
            law: { additionalFormatRules: ['statute_citation'] },
            medicine: { additionalFormatRules: ['drug_nomenclature'] },
            languages: { additionalFormatRules: ['transliteration'] },
            // 19BO+ · P2-1: essay-heavy disciplines get their citation/framework rules.
            humanities: { additionalFormatRules: ['text_evidence', 'historical_context'] },
            social_science: { additionalFormatRules: ['empirical_citation', 'quantitative_report'] },
            business: { additionalFormatRules: ['case_framework', 'financial_precision'] },
            arts: { additionalFormatRules: ['visual_analysis', 'movement_context'] },
        },
    },
    {
        id: 'mindmap',
        dependsOn: [],
        alwaysOn: false,
        generated: true,
        generates: 'mindmaps',
        defaultVolume: { min: 1, max: 1, unit: 'per-case' },
        family: 'generative',
        perChapterTarget: { min: 1, ideal: 1, max: 1 },
        label: { en: 'Mind Map', zh: '思维导图' },
        icon: '⋈',
        description: { en: 'Collapsible tree of frameworks per case.', zh: '每个 case 一棵可折叠的框架树。' },
        promptFragment: 'Generate one mindmap per case: { caseId, title, root: { label, children: [...] } }. Trees represent framework hierarchies, NOT causal chains.',
        generationTiming: 'initial',
        outlineLevel: ['chapter', 'concept'],
        crossChapterAllowed: false,
        // ★ mindmap = 1 per chapter, full coverage expected → no soft escape.
        acceptsOmissionReason: false,
        regenContext: MINDMAP_REGEN,
        auditInvariants: [
            { kind: 'tag_in_outline', field: 'caseIds' },
            { kind: 'tag_in_outline', field: 'conceptIds' },
            { kind: 'min_per_chapter', quantity: 1 },
            // ★ 100% coverage · mindmap must reach every concept (v4 §13.3 lecture_notes
            // analogue · mindmap is the visual scaffold so coverage matters).
            { kind: 'concept_coverage', scope: 'chapter', min: 1.0 },
        ],
    },
    {
        id: 'lecture_notes',
        dependsOn: [],
        alwaysOn: false,
        generated: true,
        generates: 'lecture_notes',
        defaultVolume: { min: 1, max: 1, unit: 'per-case' },
        family: 'generative',
        perChapterTarget: { min: 1, ideal: 1, max: 1 },
        label: { en: 'Lecture Notes', zh: '讲义摘要' },
        icon: '☰',
        description: { en: 'AI summary of each case with key points + sources.', zh: '每个 case 的 AI 摘要，含要点与原文出处。' },
        promptFragment: 'Generate one lecture_notes entry per case: { topic, caseIds[], content (paragraph), keyPoints[], source }. Cite the PDF page when possible.',
        generationTiming: 'initial',
        outlineLevel: ['chapter', 'concept'],
        crossChapterAllowed: false,
        acceptsOmissionReason: true,
        regenContext: LECTURE_REGEN,
        // v4-rewrite §13.3 lecture_notes example · 100% concept coverage required.
        auditInvariants: [
            { kind: 'tag_in_outline', field: 'caseIds' },
            { kind: 'tag_in_outline', field: 'conceptIds' },
            { kind: 'concept_coverage', scope: 'chapter', min: 1.0 },
            { kind: 'min_per_chapter', quantity: 1 },
            { kind: 'format_validity', ruleId: 'source_ref' },
            { kind: 'omission_rate', max: 0.2 },
        ],
    },
    {
        id: 'mock_exam',
        dependsOn: ['quiz_mcq'],
        alwaysOn: false,
        generated: false,
        generates: null,
        defaultVolume: null,
        family: 'mock',
        perChapterTarget: null,
        label: { en: 'Mock Exam', zh: '模拟考试' },
        icon: '◷',
        description: { en: 'Full-length timed mock drawn from the quiz bank.', zh: '完整长度限时模考，从题库抽样。' },
        promptFragment: '', // No AI work — runtime only.
        // ★ Only 'usage'-timed entry · initial ingest skips · /ingest/generate-mock
        // generates on demand (v4-rewrite §12).
        generationTiming: 'usage',
        // Mock papers reference outline freely (chapters/topics/concepts as filter
        // scope), but the items themselves are pulled from existing mcq/tf/essay/
        // freeResp banks — no new ItemTags emitted at this layer.
        outlineLevel: [],
        crossChapterAllowed: true,
        acceptsOmissionReason: false,
        regenContext: MOCK_REGEN,
        // Mock paper format validation lives in MockPaperSpecV2 schema. No audit
        // pass beyond that — papers are independent snapshots, not dedup'd.
        auditInvariants: [],
    },
    {
        id: 'wrong_answers',
        dependsOn: ['quiz_mcq'],
        alwaysOn: false,
        generated: false,
        generates: null,
        defaultVolume: null,
        family: 'runtime',
        perChapterTarget: null,
        label: { en: 'Wrong Answers', zh: '错题本' },
        icon: '⚠',
        description: { en: 'Auto-tracked review list with proficiency-aware revisits.', zh: '自动追踪错题，按掌握度智能复习。' },
        promptFragment: '',
        // runtime tracker · no AI generation · no regen · no audit.
        generationTiming: 'initial',
        outlineLevel: [],
        crossChapterAllowed: false,
        acceptsOmissionReason: false,
        regenContext: DEFAULT_REGEN_CONTEXT_SPEC,
        auditInvariants: [],
    },
    {
        id: 'guide',
        dependsOn: [],
        alwaysOn: false,
        generated: true,
        generates: 'guide',
        defaultVolume: null,
        family: 'generative',
        perChapterTarget: null,
        label: { en: 'Guide', zh: '使用指引' },
        icon: '★',
        description: { en: 'AI-tailored study plan from now until exam day.', zh: 'AI 按你的考试日期生成的备考计划。' },
        promptFragment: 'Generate a study guide tailored to the exam date and components above: day-by-day cards (today → exam) + per-module tips + final-day exam advice.',
        generationTiming: 'initial',
        // Guide is a single document, not item-tagged data — outlineLevel is `[]`
        // (no item-level ItemTags emitted). The guide's prompt still receives the
        // full outline via regenContext.needsFullOutline=true; this field only
        // describes what tags the audit pass would enforce, and audit is skipped
        // for single-doc generated entries (defaultVolume === null).
        outlineLevel: [],
        crossChapterAllowed: true,
        acceptsOmissionReason: false,
        regenContext: MOCK_REGEN, // pulls from full course like mock — top-down composite
        auditInvariants: [], // single doc; format checks in guide schema
    },
    {
        id: 'proof_drill',
        dependsOn: [],
        alwaysOn: false,
        generated: true,
        generates: 'proof_drills',
        defaultVolume: { min: 5, max: 12, unit: 'per-course' },
        family: 'discrimination',
        perChapterTarget: { min: 2, ideal: 3, max: 5 },
        label: { en: 'Proof Drill', zh: '证明推导' },
        icon: '∎',
        description: {
            en: 'Step-by-step derivation prompts for STEM courses (theorems, algorithms, proofs).',
            zh: '推导步骤练习——数学证明、算法正确性、物理推导等。',
        },
        promptFragment: 'Generate proof_drill items. Each: { id (starts with "proof"), caseId, prompt (theorem/problem statement, $...$ for math), keySteps[] (3-12 ordered WorkedStep objects), commonMistakes[]?, difficulty }. Each WorkedStep is { intermediate (the equation/claim of this step, $...$ for math), justification (one sentence: why this step is valid — cite axiom/theorem/previous step/definition), prerequisiteRef? (optional pointer: glossary id, "step 2", or theorem name) }. Resist cramming intermediate + justification into one string — keep them separate fields. Target 5–12 drills per course.',
        generationTiming: 'initial',
        outlineLevel: ['chapter', 'concept'],
        // STEM proofs may legitimately span chapters (e.g. proving 2 theorems together).
        crossChapterAllowed: true,
        acceptsOmissionReason: true,
        regenContext: SUBJECTIVE_REGEN,
        // 19BH+2 Day 2 Fix I-6 demo · proof_drill is LaTeX-heavy by definition.
        // STEM field gets the latex_escape rule keyed in; non-STEM fields see no
        // extra rule (current global injection still covers them — see
        // appendFormatRule()). 19BO+ collapsed the 5 old STEM discipline keys
        // (mathematics/CS/physics/engineering/chemistry) into the single `stem`
        // broad field.
        disciplineProfiles: {
            stem: { additionalFormatRules: ['latex_escape'] },
        },
        auditInvariants: [
            { kind: 'tag_in_outline', field: 'caseIds' },
            { kind: 'tag_in_outline', field: 'conceptIds' },
            { kind: 'min_per_chapter', quantity: 2 },
            { kind: 'dedup_by_concept', scope: 'course', textSimilarityThreshold: 0.85 },
            { kind: 'omission_rate', max: 0.3 },
        ],
    },
    {
        id: 'free_response',
        dependsOn: [],
        alwaysOn: false,
        generated: true,
        generates: 'freeResponses',
        defaultVolume: { min: 6, max: 18, unit: 'per-course' },
        family: 'synthesis',
        perChapterTarget: { min: 2, ideal: 3, max: 5 },
        label: { en: 'Free Response', zh: '自由作答' },
        icon: '✍',
        description: {
            en: 'Open-ended items where the user writes their own answer, then reveals the reference and self-grades against a rubric. Three kinds: short answer, calculation, translation.',
            zh: '主观作答：用户先写自己的答案，再对照参考答案与思路自评。包含简答 / 计算 / 翻译三种形态。',
        },
        promptFragment: 'Generate free_response items as a discriminated union by `kind`. Shared fields: id (starts with "fr"), caseId, prompt, scaffold[]?, hints[]?, referenceAnswer, reasoning (separate from referenceAnswer — the *thinking process*, not the *result*), selfAssessmentRubric[] (1-6 dimensions, each {dimension, criteria}), difficulty. kind="short_answer": no extra fields. kind="calculation": add finalValue {type: numerical|expression|set|tuple|string, value, units?, tolerance?} and optional workedSteps[]. kind="translation": add sourceText + targetLanguage. NEVER set aiScore — that field is reserved for premium grader. Mix kinds appropriate to the discipline (math course → mostly calculation; law course → mostly short_answer; language course → mostly translation). Target 6-18 per course.',
        generationTiming: 'initial',
        outlineLevel: ['chapter', 'concept'],
        crossChapterAllowed: true,
        acceptsOmissionReason: true,
        regenContext: SUBJECTIVE_REGEN,
        auditInvariants: [
            { kind: 'tag_in_outline', field: 'caseIds' },
            { kind: 'tag_in_outline', field: 'conceptIds' },
            { kind: 'min_per_chapter', quantity: 2 },
            { kind: 'dedup_by_concept', scope: 'course', textSimilarityThreshold: 0.85 },
            { kind: 'omission_rate', max: 0.3 },
        ],
        // 19BO+ · DEBT-19BO+-DISCIPLINE-WIREUP: free_response spans law (short_answer),
        // medicine (short_answer/calculation) and languages (translation) per its own
        // promptFragment — all three disciplines get their rules.
        disciplineProfiles: {
            law: { additionalFormatRules: ['statute_citation'] },
            medicine: { additionalFormatRules: ['drug_nomenclature'] },
            languages: { additionalFormatRules: ['transliteration'] },
            // 19BO+ · P2-1: free-response items share essay's discipline rules.
            humanities: { additionalFormatRules: ['text_evidence', 'historical_context'] },
            social_science: { additionalFormatRules: ['empirical_citation', 'quantitative_report'] },
            business: { additionalFormatRules: ['case_framework', 'financial_precision'] },
            arts: { additionalFormatRules: ['visual_analysis', 'movement_context'] },
        },
    },
    {
        id: 'reading_passage',
        dependsOn: ['quiz_mcq'],
        alwaysOn: false,
        generated: true,
        generates: 'readingPassages',
        defaultVolume: { min: 3, max: 8, unit: 'per-course' },
        family: 'discrimination',
        perChapterTarget: { min: 1, ideal: 2, max: 3 },
        label: { en: 'Reading Passage', zh: '阅读理解' },
        icon: '☷',
        description: {
            en: 'A long passage paired with multiple comprehension questions. Generic English reading — IELTS / 考研英语 specialization is a future template.',
            zh: '长篇阅读 + 配套选择题/判断题。通用英语阅读——雅思 / 考研英语特化模板是后期工作。',
        },
        promptFragment: 'Generate reading_passage items. Each: { id (starts with "rp"), caseId, title, passage (300-800 word markdown · authentic register · stays within source-material scope), questionRefs[] (≥3 ids referencing mcqs/trueFalse you generate elsewhere; cross-link to the same caseId). Generic English reading; do NOT specialize for IELTS / TOEFL / 考研 — those are future templates. Target 3-8 passages per course.',
        generationTiming: 'initial',
        // ★ Only [chapter] (passage spans topics/concepts; questionRefs handle finer tags).
        outlineLevel: ['chapter'],
        crossChapterAllowed: true,
        // Passages are single coherent texts · no partial coverage soft escape.
        acceptsOmissionReason: false,
        regenContext: SUBJECTIVE_REGEN,
        auditInvariants: [
            { kind: 'tag_in_outline', field: 'caseIds' },
            { kind: 'min_per_chapter', quantity: 1 },
            // questionRefs[] must reference items that share the same caseId —
            // shared_concepts lint enforces this cross-reference integrity.
            { kind: 'format_validity', ruleId: 'shared_concepts' },
        ],
        // 19BO+ · DEBT-19BO+-DISCIPLINE-WIREUP: passages are the core languages-exam
        // artifact (考研/雅思/TOEFL reading). Law/medicine courses rarely use long
        // reading passages, so only languages is keyed (conservative).
        disciplineProfiles: {
            languages: { additionalFormatRules: ['transliteration'] },
        },
    },
];
const BY_ID = new Map(COMPONENT_MANIFEST.map((e) => [e.id, e]));
export function getComponent(id) {
    const entry = BY_ID.get(id);
    if (!entry)
        throw new Error(`Unknown ComponentId: ${id}`);
    return entry;
}
/** Resolve transitive dependencies. Given a user-picked set, expand to include all dependsOn. */
export function expandWithDependencies(picked) {
    const out = new Set();
    for (const entry of COMPONENT_MANIFEST)
        if (entry.alwaysOn)
            out.add(entry.id);
    const stack = [...picked];
    while (stack.length > 0) {
        const id = stack.pop();
        if (out.has(id))
            continue;
        out.add(id);
        const entry = getComponent(id);
        for (const dep of entry.dependsOn)
            if (!out.has(dep))
                stack.push(dep);
    }
    return COMPONENT_MANIFEST.map((e) => e.id).filter((id) => out.has(id));
}
/** Components whose data the AI must produce, given an enabled set. */
export function generatedComponents(enabled) {
    const set = new Set(enabled);
    return COMPONENT_MANIFEST.filter((e) => set.has(e.id) && e.generated);
}
/** Group enabled components by cognitive family (Session 19AA · §13).
 *  Used by frontend dashboard to render family-grouped chapter breakdown
 *  and by ingest orchestrator to schedule per-family LLM calls. */
export function componentsByFamily(enabled) {
    const set = new Set(enabled);
    const out = {
        generative: [],
        discrimination: [],
        synthesis: [],
        mock: [],
        runtime: [],
    };
    for (const entry of COMPONENT_MANIFEST) {
        if (!set.has(entry.id))
            continue;
        out[entry.family].push(entry);
    }
    return out;
}
/**
 * 19BH Day 2 Step 2.3 · v4 helper · filter manifest entries by their
 * declared `generationTiming`. The ingest workflow (Day 3 Step 3.4 rewire)
 * calls this with `'initial'` to scope Phase 3 stages and skip `mock_exam`
 * (the only `'usage'` entry today).
 *
 * Why declarative (vs hard-coded "skip mock_exam"):
 *   - New 'usage'-timed components add themselves automatically (extensibility
 *     checkpoint plan §12.1 + §12.4).
 *   - Tests assert that exactly the v4-declared usage stages are filtered out.
 *     Drift caught at test time, not at runtime.
 */
export function manifestByTiming(timing) {
    return COMPONENT_MANIFEST.filter((e) => e.generationTiming === timing);
}
//# sourceMappingURL=component-manifest.js.map